import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { useWorkspaceSettings } from "./api/use-workspace-settings";

export interface TerminologyEntityConfig {
  singular: string;
  plural: string;
}

export interface TerminologyConfig {
  climateAction: TerminologyEntityConfig;
  project: TerminologyEntityConfig;
  task: TerminologyEntityConfig;
}

/**
 * Hardcoded German defaults used as ultimate fallback (e.g. before i18n is ready).
 * @deprecated For frontend use, prefer getDefaultTerminologyConfig() which is locale-aware.
 * This constant is kept for backend compatibility.
 */
export const DEFAULT_TERMINOLOGY_CONFIG: TerminologyConfig = {
  climateAction: {
    singular: "Maßnahme",
    plural: "Maßnahmen",
  },
  project: {
    singular: "Projekt",
    plural: "Projekte",
  },
  task: {
    singular: "Aufgabe",
    plural: "Aufgaben",
  },
};

/**
 * Gets locale-aware default terminology config using i18n.
 * Falls back to DEFAULT_TERMINOLOGY_CONFIG if i18n is not available or translations are missing.
 */
export function getDefaultTerminologyConfig(i18nInstance?: { t: (key: string) => string }): TerminologyConfig {
  // If i18n is provided, use it to get locale-aware defaults
  if (i18nInstance) {
    try {
      const climateActionSingular = i18nInstance.t("terminology.climateAction.singular");
      const climateActionPlural = i18nInstance.t("terminology.climateAction.plural");
      const projectSingular = i18nInstance.t("terminology.project.singular");
      const projectPlural = i18nInstance.t("terminology.project.plural");
      const taskSingular = i18nInstance.t("terminology.task.singular");
      const taskPlural = i18nInstance.t("terminology.task.plural");

      // Check if any translation returned the key itself (meaning translation not found)
      // If so, fall back to hardcoded defaults
      const keys = [
        "terminology.climateAction.singular",
        "terminology.climateAction.plural",
        "terminology.project.singular",
        "terminology.project.plural",
        "terminology.task.singular",
        "terminology.task.plural",
      ];
      const values = [
        climateActionSingular,
        climateActionPlural,
        projectSingular,
        projectPlural,
        taskSingular,
        taskPlural,
      ];

      // If any value equals its key, translation wasn't found - fall back to defaults
      const hasMissingTranslation = keys.some((key, index) => values[index] === key);

      if (!hasMissingTranslation) {
        // Verify we got actual translations (not empty strings)
        const hasValidTranslations = values.every((value) => value && typeof value === "string" && value.length > 0);

        if (hasValidTranslations) {
          return {
            climateAction: {
              singular: climateActionSingular,
              plural: climateActionPlural,
            },
            project: {
              singular: projectSingular,
              plural: projectPlural,
            },
            task: {
              singular: taskSingular,
              plural: taskPlural,
            },
          };
        }
      }
    } catch {
      // Fall back to hardcoded defaults if translation fails
    }
  }
  return DEFAULT_TERMINOLOGY_CONFIG;
}

export const useWorkspaceTerminology = (workspaceId: string): TerminologyConfig => {
  const { data: settings } = useWorkspaceSettings({ workspaceId });
  const { t } = useTranslation("common");

  const localeDefaults = useMemo((): TerminologyConfig => {
    // Get translations for the current language
    // i18next will fall back to German if translations aren't available, but we check
    // if the returned value equals the key to detect missing translations
    const result = getDefaultTerminologyConfig({ t: (key: string) => t(key as never) });

    // If current language is not German and result matches German defaults,
    // it might mean translations aren't loaded yet - but we'll trust i18next's fallback
    // The key check in getDefaultTerminologyConfig should catch actual missing translations
    return result;
  }, [t]);

  return useMemo(() => {
    if (settings?.terminologyConfig) {
      try {
        const config =
          typeof settings.terminologyConfig === "string"
            ? (JSON.parse(settings.terminologyConfig) as TerminologyConfig)
            : (settings.terminologyConfig as TerminologyConfig);

        // Check if saved config matches current locale defaults
        // If it matches, treat it as not customized and use locale defaults instead
        const matchesCurrentLocaleDefaults =
          config.climateAction?.singular === localeDefaults.climateAction.singular &&
          config.climateAction?.plural === localeDefaults.climateAction.plural &&
          config.project?.singular === localeDefaults.project.singular &&
          config.project?.plural === localeDefaults.project.plural &&
          config.task?.singular === localeDefaults.task.singular &&
          config.task?.plural === localeDefaults.task.plural;

        if (matchesCurrentLocaleDefaults) {
          // Saved values match current locale defaults, use locale-aware defaults
          return localeDefaults;
        }

        // Also check if saved config matches German defaults (legacy saved defaults)
        // If it matches German defaults, treat it as "not customized" and use current locale defaults
        // This handles cases where German defaults were saved before locale-aware system was implemented
        const matchesGermanDefaults =
          config.climateAction?.singular === DEFAULT_TERMINOLOGY_CONFIG.climateAction.singular &&
          config.climateAction?.plural === DEFAULT_TERMINOLOGY_CONFIG.climateAction.plural &&
          config.project?.singular === DEFAULT_TERMINOLOGY_CONFIG.project.singular &&
          config.project?.plural === DEFAULT_TERMINOLOGY_CONFIG.project.plural &&
          config.task?.singular === DEFAULT_TERMINOLOGY_CONFIG.task.singular &&
          config.task?.plural === DEFAULT_TERMINOLOGY_CONFIG.task.plural;

        if (matchesGermanDefaults) {
          // Saved values are just German defaults (legacy), use current locale defaults
          return localeDefaults;
        }

        // Validate and merge with locale-aware defaults
        // This is truly custom terminology that differs from both current locale and German defaults
        return {
          climateAction: {
            singular: config.climateAction?.singular || localeDefaults.climateAction.singular,
            plural: config.climateAction?.plural || localeDefaults.climateAction.plural,
          },
          project: {
            singular: config.project?.singular || localeDefaults.project.singular,
            plural: config.project?.plural || localeDefaults.project.plural,
          },
          task: {
            singular: config.task?.singular || localeDefaults.task.singular,
            plural: config.task?.plural || localeDefaults.task.plural,
          },
        };
      } catch {
        return localeDefaults;
      }
    }
    return localeDefaults;
  }, [settings?.terminologyConfig, localeDefaults]);
};
